import { useState } from 'react'
import { HelpCircle, Users } from 'lucide-react'
import { useI18n } from '../i18n'
import { Button, Sheet } from '../ui/kit'
import { LegendPlate, legendMarkers } from './BookingBadges'
import { HOLIDAY_ATTRIBUTION } from '../lib/holidays'

/**
 * Was die Zeichen auf einem Buchungsbalken bedeuten.
 *
 * WARUM ES SIE BRAUCHT: die Symbole erscheinen nur, wenn etwas zu tun ist — das ist
 * die richtige Regel, aber sie ist unsichtbar. Wer einen Balken ohne Zeichen sieht,
 * weiß nicht, ob dort alles erledigt ist oder ob der Balken nur zu schmal war. Genau
 * das steht deshalb als erster Satz in der Legende, noch vor den einzelnen Symbolen:
 * es ist die Information, die man nirgends erraten kann.
 *
 * WARUM ALS BLATT UND NICHT ALS FUSSZEILE: die Ferienlegende steht in der Fußzeile des
 * Kalenders und ist dort ab `lg` sichtbar — auf dem Telefon also nie. Eine Legende,
 * die genau auf dem Gerät fehlt, auf dem die Symbole am kleinsten sind, ist keine.
 * Das Blatt ist von überall erreichbar und kostet im Kalender einen einzigen Knopf.
 *
 * WARUM DIE SYMBOLE NICHT HIER AUFGEZÄHLT WERDEN: `legendMarkers()` erzeugt sie aus
 * derselben Liste, aus der die Balken sie beziehen. Eine handgeschriebene Legende
 * stimmt am Tag ihrer Entstehung und danach nie wieder.
 */
export default function CalendarLegend({ variant = 'button' }: {
  /** `link` ist die Textfassung für die Fußzeile unter dem Kalender. */
  variant?: 'button' | 'link'
}) {
  const { t, lang } = useI18n()
  const de = lang === 'de'
  const [open, setOpen] = useState(false)

  const markers = legendMarkers()

  return (
    <>
      {/*
        Beschriftet, nicht nur bebildert.
        Die erste Fassung war ein blasses Fragezeichen zwischen anderen Symbolknöpfen —
        der Betreiber hat sie zweimal gesucht und nicht gefunden. Ein Symbol, das eine
        Erklärung öffnet, muss selbst keine Erklärung brauchen. Auf schmalen Geräten
        bleibt es beim Zeichen, dort ist der Platz begründet; sonst steht das Wort daneben.
      */}
      {variant === 'link' ? (
        <button
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1 underline underline-offset-2 hover:text-fg"
        >
          <HelpCircle size={13} />
          {de ? 'Zeichenerklärung' : 'Legend'}
        </button>
      ) : (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setOpen(true)}
          aria-label={de ? 'Zeichenerklärung' : 'Legend'}
          title={de ? 'Was bedeuten die Zeichen?' : 'What do the symbols mean?'}
        >
          <HelpCircle size={18} />
          <span className="hidden md:inline">{de ? 'Zeichen' : 'Legend'}</span>
        </Button>
      )}

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title={de ? 'Zeichenerklärung' : 'Legend'}
        footer={
          <Button variant="primary" className="flex-1" onClick={() => setOpen(false)}>
            {de ? 'Verstanden' : 'Got it'}
          </Button>
        }
      >
        {/* Die Regel zuerst — sie erklärt die Abwesenheit von Symbolen, und die ist
            der häufigere Fall. */}
        <p className="text-sm bg-surface-2 rounded-2xl px-3 py-2.5">
          {de
            ? 'Ein Zeichen erscheint nur, wenn etwas zu tun ist. Ein Balken ohne Zeichen heißt: Anzahlung da, Rechnung bezahlt, nichts offen.'
            : 'A symbol only appears when something needs doing. A bar without symbols means: deposit received, invoice paid, nothing open.'}
        </p>

        <ul className="space-y-2.5">
          {markers.map((m) => (
            <li key={`${m.key}:${m.tone}`} className="flex items-start gap-3 text-sm">
              <span className="mt-0.5"><LegendPlate marker={m} /></span>
              <span className="min-w-0">{t(m.labelKey)}</span>
            </li>
          ))}
          <li className="flex items-start gap-3 text-sm">
            <span className="w-[18px] flex justify-center shrink-0 mt-0.5 text-muted">
              <Users size={13} strokeWidth={2.5} />
            </span>
            <span className="min-w-0">
              {de
                ? '„2+1" — zwei Erwachsene und ein Kind. Steht nur auf höheren Zeilen.'
                : '“2+1” — two adults and one child. Only shown on taller rows.'}
            </span>
          </li>
        </ul>

        <p className="text-xs text-muted">
          {de
            ? 'Auf schmalen Balken ist nicht für jedes Zeichen Platz. Dann bleibt das dringendste stehen — überfällige Rechnung vor offener Anzahlung vor fehlender Rechnung. Der vollständige Stand steht immer im Kurzhinweis, wenn Sie den Balken berühren, und im Buchungsblatt.'
            : 'A narrow bar has no room for every symbol. The most urgent one stays — overdue invoice before open deposit before missing invoice. The full picture is always in the tooltip and in the booking sheet.'}
        </p>

        {/* Die Griffe erklären sich am wenigsten von selbst: sie sind unsichtbar, bis
            man sie trifft, und auf schmalen Balken gibt es sie bewusst gar nicht. Wer
            das nicht weiß, hält den fehlenden Griff für einen Fehler. */}
        <div className="pt-2 border-t border-border space-y-2.5">
          <h3 className="text-sm font-medium">
            {de ? 'Am Balken ziehen' : 'Dragging a bar'}
          </h3>
          <p className="text-sm">
            {de
              ? 'In der Mitte anfassen verschiebt die ganze Buchung — auch in eine andere Einheit. An den beiden Enden anfassen ändert Anreise oder Abreise. Auf dem Telefon beginnt beides erst, wenn der Finger kurz liegen bleibt; vorher scrollt der Kalender.'
              : 'Grab the middle to move the whole booking, including into another unit. Grab either end to change the arrival or the departure. On a phone both only start once your finger has rested for a moment; before that the calendar scrolls.'}
          </p>
          <p className="text-xs text-muted">
            {de
              ? 'Kurze Balken haben keine Griffe: dort ist der Platz für das Verschieben reserviert, und das ist im Kalender der einzige Weg. Die Daten stehen immer auch im Buchungsblatt. Abgerechnete Buchungen lassen sich gar nicht ziehen — dafür muss zuerst die Rechnung storniert werden.'
              : 'Short bars have no end grips: the room there is reserved for moving, which is the only gesture without an alternative. The dates are always in the booking sheet as well. Invoiced bookings cannot be dragged at all — cancel the invoice first.'}
          </p>
        </div>

        {/* Die Ferienleiste gehört ebenfalls hierher: sie steht im selben Kalender,
            und ihre Erklärung war bisher nur am großen Bildschirm zu sehen. */}
        <div className="pt-2 border-t border-border space-y-2.5">
          <h3 className="text-sm font-medium">
            {de ? 'Über dem Datum' : 'Above the date'}
          </h3>
          <div className="flex items-start gap-3 text-sm">
            <span className="w-[18px] flex justify-center shrink-0 mt-2">
              <span className="w-3 h-1 rounded-full bg-warn/70" />
            </span>
            <span className="min-w-0">
              {de
                ? 'Schulferien in Ihren Gästemärkten. Je breiter der Balken, desto mehr Märkte haben gleichzeitig frei — ein Hinweis für die Preisgestaltung.'
                : 'School holidays in your guest markets. The wider the bar, the more markets are off at once — a hint for pricing.'}
            </span>
          </div>
          <div className="flex items-start gap-3 text-sm">
            <span className="w-[18px] flex justify-center shrink-0 mt-2">
              <span className="w-1 h-1 rounded-full bg-danger" />
            </span>
            <span className="min-w-0">
              {de ? 'Feiertag in mindestens einem Gästemarkt.' : 'Public holiday in at least one guest market.'}
            </span>
          </div>
          <p className="text-[10px] text-muted">{HOLIDAY_ATTRIBUTION}</p>
        </div>
      </Sheet>
    </>
  )
}

/** Die Textfassung für die Fußzeile — dort, wo man eine Legende sucht. */
export function LegendLink() {
  return <CalendarLegend variant="link" />
}
